import type {
  EventOpportunityDefaults,
  SelectOption,
} from "./opportunityFormTypes";

export const DATE_MAX_FOUR_DIGIT_YEAR = "9999-12-31";

export const ISO_DATE_WITH_FOUR_DIGIT_YEAR = /^\d{4}-\d{2}-\d{2}$/;

export const isFourDigitYearDate = (value: unknown): boolean =>
  typeof value !== "string" ||
  value === "" ||
  ISO_DATE_WITH_FOUR_DIGIT_YEAR.test(value);

export const flattenTranslationValues = (
  source: Record<string, unknown>,
  prefix = "",
): Record<string, string> => {
  return Object.entries(source).reduce(
    (acc: Record<string, string>, [key, value]) => {
      const fullKey = prefix ? `${prefix}.${key}` : key;
      if (typeof value === "string") {
        acc[value] = fullKey;
      } else if (value && typeof value === "object") {
        Object.assign(
          acc,
          flattenTranslationValues(value as Record<string, unknown>, fullKey),
        );
      }
      return acc;
    },
    {},
  );
};

export const formatOptionLabelForUi = (label: unknown): string => {
  const rawText = String(label ?? "");
  const hasUnderscore = rawText.includes("_");
  const text = rawText
    .replace(/_+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (!text) return "";

  const shouldFormat =
    hasUnderscore ||
    text.split(" ").some((word) => /^[A-Z0-9]{4,}$/.test(word));

  if (!shouldFormat) return text;

  return text
    .split(" ")
    .map((word) => {
      if (!hasUnderscore && /^[A-Z0-9]{2,3}$/.test(word)) return word;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
};

export const formatOptionLabelsForUi = <T extends SelectOption>(options: T[]): T[] =>
  options.map((option) => ({
    ...option,
    label: formatOptionLabelForUi(option.label ?? option.value),
  }));

export const normalizeOptionsSource = (source?: string) =>
  String(source || "")
    .trim()
    .replace(/^https?:\/\/[^/]+\//, "")
    .replace(/^\/+/, "")
    .replace(/^api\/v\d+\//, "");

export const getEventDefaultForField = (
  fieldKey: string,
  eventDefaults?: EventOpportunityDefaults,
): string | undefined => {
  if (!eventDefaults) return undefined;

  const normalizedKey = String(fieldKey || "").toLowerCase();
  const filledDefault = (value?: string) => {
    const normalized = String(value ?? "").trim();
    return normalized ? normalized : undefined;
  };

  if (normalizedKey === "country") return filledDefault(eventDefaults.country);
  if (normalizedKey === "city") return filledDefault(eventDefaults.city);
  if (normalizedKey === "venue") return filledDefault(eventDefaults.venue);
  if (normalizedKey === "sport" || normalizedKey === "combatsport" || normalizedKey === "combat_sport") {
    return filledDefault(eventDefaults.sport);
  }
  if (normalizedKey === "weightclass" || normalizedKey === "weight_class") {
    return filledDefault(eventDefaults.weightClass);
  }
  if (normalizedKey === "eventdate" || normalizedKey === "event_date") {
    return filledDefault(eventDefaults.eventDate);
  }

  return undefined;
};
